import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Alert,
  Container,
  Tabs,
  Tab,
  CircularProgress,
  Pagination,
} from '@mui/material';
import ShapeBackground from '../common/ShapeBackground';
import { useListTransactions } from '../../hooks/useListTransactions';
import TransactionItem from './TransactionItem';

const TransactionsPage: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useListTransactions(page);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const filteredTransactions = data?.results.filter(transaction => {
    if (currentTab === 0) return true;
    return currentTab === 1 ? transaction.type === 'DEPOSIT' : transaction.type === 'WITHDRAWAL';
  }) || [];

  return (
    <Box sx={{ 
      position: 'relative',
      minHeight: '100vh',
      display: 'flex',
      pt: 8,
    }}>
      <ShapeBackground />
      <Container maxWidth="lg" sx={{ 
        position: 'relative', 
        zIndex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        height: 'calc(100vh - 120px)',
        maxHeight: '900px'
      }}>
        <Paper elevation={3} sx={{ 
          p: 4, 
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          mb: 2,
          maxHeight: 'calc(100% - 16px)'
        }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            Transactions
          </Typography>

          <Tabs 
            value={currentTab} 
            onChange={handleTabChange}
            sx={{ mb: 3 }}
          >
            <Tab label="All" />
            <Tab label="Deposits" />
            <Tab label="Withdrawals" />
          </Tabs>

          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">
              Failed to load transactions
            </Alert>
          ) : filteredTransactions.length === 0 ? (
            <Alert severity="info">
              No transactions found.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <Card sx={{ 
                backdropFilter: 'blur(20px)',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                boxShadow: '0px 2px 24px rgba(0, 0, 0, 0.1)',
                borderRadius: 3,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}>
                <CardContent sx={{ 
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  p: 0,
                  '&:last-child': {
                    pb: 0,
                  },
                }}>
                  <Box sx={{ 
                    flex: 1,
                    overflowY: 'auto',
                    p: 2,
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'rgba(0, 0, 0, 0.05)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '4px',
                      '&:hover': {
                        background: 'rgba(0, 0, 0, 0.3)',
                      },
                    },
                  }}>
                    {filteredTransactions.map((transaction, index) => (
                      <TransactionItem
                        key={transaction.id}
                        id={transaction.id}
                        accountId={transaction.accountId}
                        amount={transaction.amount}
                        merchantName={transaction.merchantName}
                        date={transaction.date}
                        type={transaction.type}
                        showDivider={index < filteredTransactions.length - 1}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
              
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={Math.ceil((data?.count || 0) / 20)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                />
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default TransactionsPage; 